import React, {useCallback, useEffect, useRef} from 'react';
import {type StateSnapshot, type VirtuosoHandle} from 'react-virtuoso';

export type LogViewerState = {
  snapshot: StateSnapshot;
  firstItemIndex: number;
};

/**
 * useVirtuosoState provides a function that captures Virtuoso scroll state and
 * forwards it to the provided `onStateSaved` on `LogViewer` unmount.
 */
export function useVirtuosoState(
  virtuosoRef: React.RefObject<VirtuosoHandle | null>,
  onStateSaved: ((state: LogViewerState) => void) | undefined
) {
  const stateRef = useRef<LogViewerState | undefined>(undefined);

  const onStateSavedRef = useRef(onStateSaved);
  // oxlint-disable-next-line react/refs
  onStateSavedRef.current = onStateSaved;

  useEffect(() => {
    return () => {
      if (stateRef.current) {
        onStateSavedRef.current?.(stateRef.current);
      }
    };
  }, []);

  const captureState = useCallback(
    (firstItemIndex: number) => {
      virtuosoRef.current?.getState(snapshot => {
        stateRef.current = {snapshot, firstItemIndex};
      });
    },
    [virtuosoRef]
  );

  return {captureState};
}
